/**
 * - Handles authentication configuration for API requests.
 * - Supports Basic Auth and Bearer Token
 * - Allows enabling/disabling auth
 * - Provides password/token visibility toggle
 */
import React, { useState } from 'react';
import { AuthState, AuthType } from './types';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  InputAdornment,
  IconButton,
  Checkbox,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

interface AuthTabProps {
  auth: AuthState;
  setAuth: React.Dispatch<React.SetStateAction<AuthState>>;
  viewMode: boolean;
}

const AuthTab = ({ auth, setAuth, viewMode }: AuthTabProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleTypeChange = (type: AuthType) => {
    if (viewMode) return;
    setAuth((prev) => ({
      ...prev,
      type,
      bearer: prev.bearer || { token: '', prefix: 'Bearer', enabled: false },
      basic: prev.basic || { username: '', password: '', enabled: false },
    }));
  };

  const labelBoxStyle = {
    minWidth: '130px',
    fontWeight: 'bold',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    mb: '-4px',
  };

  // Helper to clear mask on click
  const handleFocus = (field: 'username' | 'password' | 'token') => {
    if (viewMode) return;

    setAuth((prev) => {
      const newState = { ...prev };
      if (field === 'username' && prev.basic?.username === '******') {
        newState.basic = { ...prev.basic!, username: '' };
      } else if (field === 'password' && prev.basic?.password === '******') {
        newState.basic = { ...prev.basic!, password: '' };
      } else if (field === 'token' && prev.bearer?.token === '******') {
        newState.bearer = { ...prev.bearer!, token: '' };
      }
      return newState;
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
      <FormControl fullWidth size="small">
        <InputLabel>Auth Type</InputLabel>
        <Select
          value={auth.type}
          label="Auth Type"
          disabled={viewMode}
          onChange={(e) => handleTypeChange(e.target.value as AuthType)}
        >
          <MenuItem value="none">No Auth</MenuItem>
          <MenuItem value="basic">Basic Auth</MenuItem>
          <MenuItem value="bearer">Bearer Token</MenuItem>
        </Select>
      </FormControl>

      <Box>
        {auth.type === 'none' && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: 'center',
              mt: 2,
            }}
          >
            This request does not use any authentication.
          </Typography>
        )}

        {/* BASIC AUTH SECTION */}
        {auth.type === 'basic' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* ENABLED Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '-8px' }}>
              <Typography variant="caption" sx={labelBoxStyle}>
                ENABLED
              </Typography>
              <Checkbox
                size="small"
                disabled={viewMode}
                checked={auth.basic?.enabled || false}
                onChange={(e) =>
                  setAuth((prev) => ({
                    ...prev,
                    basic: { ...prev.basic!, enabled: e.target.checked },
                  }))
                }
                sx={{ ml: -1 }}
              />
            </Box>

            {/* USERNAME Row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography variant="caption" sx={{ ...labelBoxStyle }}>
                USERNAME
              </Typography>
              <TextField
                fullWidth
                variant="standard"
                disabled={viewMode || !auth.basic?.enabled}
                placeholder="Username"
                value={auth.basic?.username || ''}
                onFocus={() => handleFocus('username')}
                onChange={(e) =>
                  setAuth((prev) => ({
                    ...prev,
                    basic: { ...prev.basic!, username: e.target.value },
                  }))
                }
              />
            </Box>

            {/* PASSWORD Row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography variant="caption" sx={{ ...labelBoxStyle }}>
                PASSWORD
              </Typography>
              <TextField
                fullWidth
                variant="standard"
                disabled={viewMode || !auth.basic?.enabled}
                type={
                  auth.basic?.password === '******' ? 'text' : showPassword ? 'text' : 'password'
                }
                placeholder="Password"
                value={auth.basic?.password || ''}
                onFocus={() => handleFocus('password')}
                onChange={(e) =>
                  setAuth((prev) => ({
                    ...prev,
                    basic: { ...prev.basic!, password: e.target.value },
                  }))
                }
                InputProps={{
                  endAdornment: !viewMode && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                        {showPassword ? (
                          <VisibilityOff fontSize="inherit" />
                        ) : (
                          <Visibility fontSize="inherit" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        )}

        {/* BEARER TOKEN SECTION */}
        {auth.type === 'bearer' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* ENABLED Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '-8px' }}>
              <Typography variant="caption" sx={labelBoxStyle}>
                ENABLED
              </Typography>
              <Checkbox
                size="small"
                disabled={viewMode}
                checked={auth.bearer?.enabled || false}
                onChange={(e) =>
                  setAuth((prev) => ({
                    ...prev,
                    bearer: { ...prev.bearer!, enabled: e.target.checked },
                  }))
                }
                sx={{ ml: -1 }}
              />
            </Box>

            {/* TOKEN Row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography variant="caption" sx={{ ...labelBoxStyle }}>
                TOKEN
              </Typography>
              <TextField
                fullWidth
                variant="standard"
                disabled={viewMode || !auth.bearer?.enabled}
                type={auth.bearer?.token === '******' ? 'text' : showPassword ? 'text' : 'password'}
                placeholder="Your Token"
                value={auth.bearer?.token || ''}
                onChange={(e) =>
                  setAuth((prev) => ({
                    ...prev,
                    bearer: { ...prev.bearer!, token: e.target.value },
                  }))
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                        {showPassword ? (
                          <VisibilityOff fontSize="inherit" />
                        ) : (
                          <Visibility fontSize="inherit" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* PREFIX Row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography variant="caption" sx={{ ...labelBoxStyle }}>
                PREFIX
              </Typography>
              <TextField
                fullWidth
                variant="standard"
                disabled={viewMode || !auth.bearer?.enabled}
                placeholder="Bearer"
                value={auth.bearer?.prefix || ''}
                onChange={(e) =>
                  setAuth((prev) => ({
                    ...prev,
                    bearer: { ...prev.bearer!, prefix: e.target.value },
                  }))
                }
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AuthTab;
